import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Sparkles, FileText, Plus, Upload, Play, CheckCircle, AlertCircle, FileUp, Wand2 } from 'lucide-react';

const HowItWorksSection = () => {
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            id: 'generate',
            title: 'Generate',
            description: 'Describe your dataset in natural language. We create synthetic data that mirrors real-world complexity without privacy risks.',
            icon: <Wand2 className="w-6 h-6" />,
            color: 'blue'
        },
        {
            id: 'clean',
            title: 'Clean',
            description: 'Already have data? Our AI agents scan, detect anomalies, and fix inconsistencies automatically.',
            icon: <Sparkles className="w-6 h-6" />,
            color: 'blue'
        },
        {
            id: 'augment',
            title: 'Augment',
            description: 'Upload documents or knowledge bases. We enrich your dataset with domain-specific insights found in your files.',
            icon: <FileUp className="w-6 h-6" />,
            color: 'blue'
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % steps.length);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-blue-600 mb-4">
                    How it works
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    From creation to refinement, our platform handles every stage of your data lifecycle.
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-12 items-center">
                {/* Steps List */}
                <div className="w-full lg:w-1/3 space-y-6">
                    {steps.map((step, index) => (
                        <div
                            key={step.id}
                            className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${activeStep === index
                                ? 'bg-white border-blue-500 shadow-lg scale-105'
                                : 'bg-gray-50 border-transparent hover:bg-white hover:border-gray-200'
                                }`}
                            onClick={() => setActiveStep(index)}
                        >
                            <div className="flex items-center gap-4 mb-2">
                                <div className={`p-2 rounded-lg ${activeStep === index ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'
                                    }`}>
                                    {step.icon}
                                </div>
                                <h3 className={`text-xl font-bold ${activeStep === index ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {step.title}
                                </h3>
                            </div>
                            <p className={`text-sm ${activeStep === index ? 'text-gray-600' : 'text-gray-400'}`}>
                                {step.description}
                            </p>
                            {/* Progress Bar for Active Step */}
                            {activeStep === index && (
                                <div className="w-full bg-gray-100 h-1 mt-4 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: "0%" }}
                                        animate={{ width: "100%" }}
                                        transition={{ duration: 6, ease: "linear" }}
                                        className="h-full bg-blue-500"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* UI Animation Window */}
                <div className="w-full lg:w-2/3">
                    <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 min-h-[450px] flex flex-col relative text-sm font-sans">

                        {/* Mock App Header */}
                        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm z-10">
                            <div className="flex items-center gap-4">
                                <span className="text-blue-600 font-bold text-lg">DataCreator AI</span>
                                <div className="h-4 w-px bg-gray-300"></div>
                                <div className="flex items-center gap-2 text-gray-500 bg-gray-50 px-3 py-1.5 rounded-md">
                                    <FileText size={14} />
                                    <span>customer_data_v2.csv</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button className="p-2 text-gray-400 hover:text-gray-600"><CheckCircle size={18} /></button>
                                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">JD</div>
                            </div>
                        </div>

                        {/* Mock Toolbar */}
                        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-3">
                            <button className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium shadow-sm">
                                <Wand2 size={12} /> Generate
                            </button>
                            <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md text-xs font-medium">
                                <Sparkles size={12} /> Clean Data
                            </button>
                            <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md text-xs font-medium">
                                <FileUp size={12} /> Augment
                            </button>
                        </div>

                        {/* Main Grid Area */}
                        <div className="flex-grow p-4 bg-gray-50/50 relative overflow-hidden">
                            {/* Grid Header */}
                            <div className="grid grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-t-lg overflow-hidden mb-0">
                                <div className="bg-gray-100 p-2 text-xs font-semibold text-gray-500">ID</div>
                                <div className="bg-gray-100 p-2 text-xs font-semibold text-gray-500">Info</div>
                                <div className="bg-gray-100 p-2 text-xs font-semibold text-gray-500">Status</div>
                                <div className="bg-gray-100 p-2 text-xs font-semibold text-gray-500">Category</div>
                            </div>

                            {/* Dynamic Content Based on Step */}
                            <div className="bg-white border-x border-b border-gray-200 rounded-b-lg min-h-[300px] relative">
                                <AnimatePresence mode='wait'>
                                    {activeStep === 0 && ( /* GENERATE */
                                        <motion.div
                                            key="generate"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 p-4"
                                        >
                                            {/* Typing Prompt Simulation */}
                                            <motion.div
                                                initial={{ opacity: 1 }}
                                                animate={{ opacity: [1, 1, 0] }}
                                                transition={{ times: [0, 0.25, 0.33], duration: 6 }}
                                                className="absolute inset-0 bg-black/5 flex items-center justify-center z-10"
                                            >
                                                <motion.div
                                                    initial={{ scale: 0.9, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full border border-gray-200"
                                                >
                                                    <div className="text-gray-900 font-semibold mb-2 flex items-center gap-2">
                                                        <Wand2 className="text-blue-600" size={16} /> Generate Data
                                                    </div>
                                                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-600 text-sm mb-4 font-mono">
                                                        "Create a dataset of 5 e-commerce transactions..."
                                                        <motion.span
                                                            animate={{ opacity: [0, 1, 0] }}
                                                            transition={{ repeat: Infinity, duration: 0.8 }}
                                                            className="inline-block w-1.5 h-4 bg-blue-600 ml-1 align-middle"
                                                        />
                                                    </div>
                                                    <div className="flex justify-end">
                                                        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-medium">Generate</button>
                                                    </div>
                                                </motion.div>
                                            </motion.div>

                                            {/* Generated Rows Appearing */}
                                            <div className="space-y-0 mt-4">
                                                {[1, 2, 3].map((i) => (
                                                    <motion.div
                                                        key={i}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 3.1 + ((i + 2) * 0.6), duration: 0.6 }}
                                                        className="grid grid-cols-4 gap-px bg-gray-50 border-b border-gray-100"
                                                    >
                                                        <div className="bg-white p-3 text-sm text-gray-500">#{1000 + i}</div>
                                                        <div className="bg-white p-3 text-sm font-medium">Product {i}</div>
                                                        <div className="bg-white p-3 text-sm">
                                                            <span className="text-green-600 bg-green-50 px-2 py-1 rounded text-xs">Generated</span>
                                                        </div>
                                                        <div className="bg-white p-3 text-sm text-gray-500">
                                                            {i % 2 === 0 ? "Electronics" : "Clothing"}
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {activeStep === 1 && ( /* CLEAN */
                                        <motion.div
                                            key="clean"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="w-full relative h-full flex flex-col justify-start pt-8"
                                        >
                                            {/* Chat/Action Bar Simulation */}
                                            <div className="absolute top-2 left-4 right-4 bg-white shadow-lg rounded-lg border border-purple-100 p-2 flex items-center gap-2 z-20">
                                                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                                    <Sparkles size={14} />
                                                </div>
                                                <div className="text-sm text-gray-600 font-mono">
                                                    <motion.span
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ duration: 0.5 }}
                                                    >
                                                        Fix invalid email formats...
                                                    </motion.span>
                                                    <motion.span
                                                        animate={{ opacity: [0, 1, 0] }}
                                                        transition={{ repeat: Infinity, duration: 0.8 }}
                                                        className="inline-block w-0.5 h-4 bg-purple-600 ml-1 align-middle"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mt-8 space-y-0 relative">
                                                {[1, 2].map((i) => (
                                                    <div key={i} className="grid grid-cols-4 gap-px bg-gray-50 border-b border-gray-100 items-center relative">
                                                        <div className="bg-white p-3 text-sm text-gray-500">#{100 + i}</div>
                                                        <div className="bg-white p-3 text-sm font-medium">John Doe</div>
                                                        <div className="bg-white p-3 relative h-12 flex items-center">
                                                            {i === 1 ? (
                                                                <div className="relative w-full">
                                                                    {/* Original/Changed Value */}
                                                                    <motion.div
                                                                        initial={{ opacity: 1, display: "block" }}
                                                                        animate={{ opacity: 0, display: "none" }}
                                                                        transition={{ delay: 2, duration: 0.2 }}
                                                                        className="text-red-500 line-through text-xs absolute"
                                                                    >
                                                                        john.doe@gma
                                                                    </motion.div>
                                                                    <motion.div
                                                                        initial={{ opacity: 0, y: 10 }}
                                                                        animate={{ opacity: 1, y: 0 }}
                                                                        transition={{ delay: 2.2, duration: 0.3 }}
                                                                        className="text-green-600 bg-green-50 px-2 py-1 rounded text-sm w-fit"
                                                                    >
                                                                        john.doe@gmail.com
                                                                    </motion.div>

                                                                    {/* Accept/Reject Floating Buttons */}
                                                                    <motion.div
                                                                        initial={{ opacity: 0, scale: 0.8, x: 20 }}
                                                                        animate={{ opacity: 1, scale: 1, x: 0 }}
                                                                        transition={{ delay: 2.5, duration: 0.3 }}
                                                                        className="absolute left-full top-1/2 -translate-y-1/2 ml-2 flex gap-1 z-30 bg-white shadow-lg rounded-md p-1 border border-gray-100"
                                                                    >
                                                                        <motion.button
                                                                            whileHover={{ scale: 1.1 }}
                                                                            className="p-1 hover:bg-green-50 rounded text-green-600"
                                                                        >
                                                                            <CheckCircle size={16} />
                                                                        </motion.button>
                                                                        <button className="p-1 hover:bg-red-50 rounded text-red-500">
                                                                            <AlertCircle size={16} />
                                                                        </button>
                                                                    </motion.div>

                                                                    {/* Simulated Cursor Click */}
                                                                    <motion.div
                                                                        initial={{ opacity: 0, top: 20, left: 150 }}
                                                                        animate={{ opacity: [0, 1, 1, 0], top: 10, left: 160 }}
                                                                        transition={{ delay: 3.5, duration: 1 }}
                                                                        className="absolute w-4 h-4 pointer-events-none z-40"
                                                                    >
                                                                        <svg viewBox="0 0 24 24" fill="black" className="w-full h-full drop-shadow-md">
                                                                            <path d="M5.5 3.21l10.8 15.6-5.4.9-2.7 6.3-2.7-6.3-5.4-.9L5.5 3.2z" />
                                                                        </svg>
                                                                    </motion.div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-600 text-sm">jane@corp.com</span>
                                                            )}
                                                        </div>
                                                        <div className="bg-white p-3 text-sm text-gray-500">Retail</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {activeStep === 2 && ( /* AUGMENT */
                                        <motion.div
                                            key="augment"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="flex h-full"
                                        >
                                            {/* Sidebar Knowledge Base */}
                                            <motion.div
                                                initial={{ width: 0, opacity: 0 }}
                                                animate={{ width: "35%", opacity: 1 }}
                                                transition={{ duration: 0.5 }}
                                                className="bg-gray-50 border-r border-gray-200 p-4 flex flex-col gap-4 z-10"
                                            >
                                                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                                    <FileUp size={12} /> Knowledge Base
                                                </div>

                                                {/* PDF File Card */}
                                                <motion.div
                                                    initial={{ y: 20, opacity: 0 }}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    transition={{ delay: 0.5 }}
                                                    className="bg-white p-3 rounded-lg border border-blue-200 shadow-md flex items-start gap-3 relative overflow-hidden group"
                                                >
                                                    <div className="bg-red-50 p-2 rounded-lg text-red-500 shrink-0">
                                                        <FileText size={20} />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-gray-800 text-sm truncate">Medical_Policy_2025.pdf</div>
                                                        <div className="text-xs text-gray-400 mt-1">Processed</div>
                                                    </div>

                                                    {/* Scanning Line Animation */}
                                                    <motion.div
                                                        animate={{ top: ["0%", "100%", "0%"] }}
                                                        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                                                        className="absolute left-0 right-0 h-0.5 bg-blue-400/50 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                                                    />
                                                </motion.div>

                                                {/* Connecting Lines (Visual) */}
                                                <div className="flex-grow relative">
                                                    <svg className="absolute inset-0 w-full h-full overflow-visible">
                                                        <motion.path
                                                            d="M 50 20 C 100 20, 150 50, 200 50"
                                                            fill="none"
                                                            stroke="#3b82f6"
                                                            strokeWidth="2"
                                                            strokeDasharray="4 4"
                                                            initial={{ pathLength: 0, opacity: 0 }}
                                                            animate={{ pathLength: 1, opacity: 0.5 }}
                                                            transition={{ delay: 1, duration: 1 }}
                                                        />
                                                    </svg>
                                                </div>
                                            </motion.div>

                                            {/* Grid with New Column */}
                                            <div className="flex-grow bg-white flex flex-col">
                                                <div className="grid grid-cols-2 gap-px bg-gray-100 border-b border-gray-200">
                                                    <div className="p-2 text-xs font-semibold text-gray-500">Condition</div>
                                                    <motion.div
                                                        initial={{ opacity: 0, backgroundColor: "#eff6ff" }}
                                                        animate={{ opacity: 1, backgroundColor: "#ffffff" }}
                                                        transition={{ delay: 1.2 }}
                                                        className="p-2 text-xs font-semibold text-blue-600 flex items-center gap-1"
                                                    >
                                                        <Sparkles size={10} /> Policy Coverage
                                                    </motion.div>
                                                </div>

                                                {[
                                                    { cond: "Diabetes T2", cov: "Covered (Sec 4.2)" },
                                                    { cond: "Cosmetic", cov: "Excluded (Sec 9.1)" },
                                                    { cond: "Hypertension", cov: "Covered (Sec 4.2)" }
                                                ].map((row, i) => (
                                                    <div key={i} className="grid grid-cols-2 gap-px border-b border-gray-50">
                                                        <div className="p-3 text-sm text-gray-600">{row.cond}</div>

                                                        <div className="p-3 relative overflow-hidden">
                                                            <motion.div
                                                                initial={{ x: -20, opacity: 0 }}
                                                                animate={{ x: 0, opacity: 1 }}
                                                                transition={{ delay: 1.5 + (i * 0.3) }}
                                                                className={`text-sm font-medium ${row.cov.includes("Covered") ? "text-green-600" : "text-red-500"}`}
                                                            >
                                                                {row.cov}
                                                            </motion.div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Mock Footer Status */}
                        <div className="bg-white border-t border-gray-200 px-4 py-2 text-xs text-gray-400 flex justify-between">
                            <span>Ready</span>
                            <span>Ln 1, Col 1</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HowItWorksSection;
